import type {
  ConnectionInfo,
  DeskRow,
  EventEntry,
  FileEntry,
  HealthResponse,
  SessionInfo,
  SpendResponse,
} from "./types";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
    public path: string,
  ) {
    super(`API ${status} at ${path}: ${body.slice(0, 200)}`);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => "");
    throw new ApiError(resp.status, body, path);
  }
  return resp.json();
}

export const api = {
  // --- Health ---
  health: () => apiFetch<HealthResponse>("/health"),

  // --- Desks ---
  listDesks: () => apiFetch<{ desks: DeskRow[] }>("/desks"),
  getDesk: (id: string) => apiFetch<{ desk: DeskRow }>(`/desks/${id}`),
  createDesk: (opts: {
    name: string;
    agentType?: string;
    model?: string;
    systemPrompt?: string;
  }) =>
    apiFetch<{ desk: DeskRow }>("/desks", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  deleteDesk: (id: string) =>
    apiFetch<{ deleted: string }>(`/desks/${id}`, { method: "DELETE" }),
  wakeDesk: (id: string) =>
    apiFetch<{ status: string; relayUrl?: string; relayAuthToken?: string }>(
      `/desks/${id}/wake`,
      { method: "POST" },
    ),

  /**
   * Tells the orchestrator "a human is looking at this desk right now".
   * The orchestrator will keepalive the sprite in the background so the
   * user doesn't hit a cold-start when they type. Call repeatedly while
   * the view is mounted; TTL is maintained server-side.
   */
  presence: (id: string) =>
    apiFetch<{ ok: boolean }>(`/desks/${id}/presence`, { method: "POST" }),
  getConnection: (id: string) =>
    apiFetch<ConnectionInfo>(`/desks/${id}/connection`),

  // --- Files (FS API — works in any thermal state) ---
  listFiles: (deskId: string, dirPath = "/", root?: "data" | "sprite") => {
    const params = new URLSearchParams({ path: dirPath });
    if (root && root !== "data") params.set("root", root);
    return apiFetch<{ path: string; entries: FileEntry[]; count: number }>(
      `/desks/${deskId}/files?${params}`,
    );
  },
  readFile: async (
    deskId: string,
    filePath: string,
    root?: "data" | "sprite",
  ): Promise<string> => {
    const params = new URLSearchParams({ path: filePath });
    if (root && root !== "data") params.set("root", root);
    const resp = await fetch(`${BASE}/desks/${deskId}/files/read?${params}`);
    if (!resp.ok) throw new ApiError(resp.status, await resp.text(), filePath);
    return resp.text();
  },
  downloadFileUrl: (
    deskId: string,
    filePath: string,
    root?: "data" | "sprite",
  ): string => {
    const params = new URLSearchParams({ path: filePath });
    if (root && root !== "data") params.set("root", root);
    return `${BASE}/desks/${deskId}/files/read?${params}`;
  },
  /**
   * URL that streams the contents of `dirPath` back as a ZIP. Used by the
   * files panel's "Download as ZIP" actions — the browser opens this in a
   * new tab and the orchestrator's Content-Disposition triggers the save
   * dialog. Subject to the orchestrator's size cap (5000 files / 500 MB),
   * past which the response is a 413 JSON error.
   */
  downloadFolderUrl: (
    deskId: string,
    dirPath: string,
    root?: "data" | "sprite",
  ): string => {
    const params = new URLSearchParams({ path: dirPath });
    if (root && root !== "data") params.set("root", root);
    return `${BASE}/desks/${deskId}/files/download-zip?${params}`;
  },
  /**
   * Upload a single file to `dirPath` on the desk. Optional `relativeDir`
   * is appended to `dirPath` (forward-slash-joined, no leading slash), so
   * folder uploads can re-create their nested structure without any extra
   * mkdir round-trips — the orchestrator's `mkdir=true` flag on the
   * Sprites FS write API materialises the chain.
   */
  uploadFile: async (
    deskId: string,
    dirPath: string,
    file: File,
    relativeDir?: string,
  ): Promise<{ files: string[]; count: number }> => {
    const cleanRel = (relativeDir ?? "").replace(/^\/+|\/+$/g, "");
    const finalDir = cleanRel
      ? `${dirPath.replace(/\/+$/, "")}/${cleanRel}`
      : dirPath;
    const params = new URLSearchParams();
    params.set("path", finalDir);
    params.set("filename", file.name);
    const resp = await fetch(`${BASE}/desks/${deskId}/files/upload?${params}`, {
      method: "POST",
      body: await file.arrayBuffer(),
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!resp.ok) throw new ApiError(resp.status, await resp.text(), dirPath);
    return resp.json();
  },
  mkdir: (deskId: string, dirPath: string) =>
    apiFetch<{ created: string }>(
      `/desks/${deskId}/files/mkdir?path=${encodeURIComponent(dirPath)}`,
      { method: "POST" },
    ),
  deleteFile: (deskId: string, filePath: string) =>
    apiFetch<{ deleted: string }>(
      `/desks/${deskId}/files?path=${encodeURIComponent(filePath)}`,
      { method: "DELETE" },
    ),

  // --- Sessions (FS API — works in any thermal state) ---
  listSessions: (deskId: string) =>
    apiFetch<SessionInfo[]>(`/desks/${deskId}/agent/sessions`),
  getSessionInfo: (deskId: string, sessionId: string) =>
    apiFetch<SessionInfo>(`/desks/${deskId}/agent/${sessionId}`),
  getEvents: (
    deskId: string,
    sessionId: string,
    opts?: { from?: number; last?: number },
  ) => {
    const params = new URLSearchParams();
    if (opts?.from !== undefined) params.set("from", String(opts.from));
    if (opts?.last !== undefined) params.set("last", String(opts.last));
    const query = params.toString() ? `?${params}` : "";
    return apiFetch<EventEntry[]>(
      `/desks/${deskId}/agent/${sessionId}/events${query}`,
    );
  },

  // --- Agent (require running sprite + relay) ---
  startAgent: (
    deskId: string,
    opts: {
      prompt: string;
      invocation?: string;
      model?: string;
      systemPrompt?: string;
    },
  ) =>
    apiFetch<{ sessionId: string; status?: string }>(
      `/desks/${deskId}/agent/start`,
      { method: "POST", body: JSON.stringify(opts) },
    ),
  sendMessage: (
    deskId: string,
    sessionId: string,
    prompt: string,
    model?: string,
  ) =>
    apiFetch<{ sessionId: string; status?: string }>(
      `/desks/${deskId}/agent/${sessionId}/message`,
      { method: "POST", body: JSON.stringify({ prompt, model }) },
    ),
  interruptAgent: (deskId: string, sessionId: string) =>
    apiFetch<{ interrupted: boolean }>(
      `/desks/${deskId}/agent/${sessionId}/interrupt`,
      { method: "POST" },
    ),
  deleteSession: (deskId: string, sessionId: string) =>
    apiFetch<{ deleted: string }>(`/desks/${deskId}/agent/${sessionId}`, {
      method: "DELETE",
    }),

  // --- Env vars ---
  listEnvVars: (deskId: string) =>
    apiFetch<{ vars: string[] }>(`/desks/${deskId}/env`),
  addEnvVar: (deskId: string, key: string, value: string) =>
    apiFetch<{ added: string }>(`/desks/${deskId}/env`, {
      method: "POST",
      body: JSON.stringify({ key, value }),
    }),
  deleteEnvVar: (deskId: string, key: string) =>
    apiFetch<{ removed: string }>(`/desks/${deskId}/env/${key}`, {
      method: "DELETE",
    }),

  // --- Sprite status (real-time thermal state) ---
  spriteStatus: (deskId: string) =>
    apiFetch<{
      spriteName: string;
      spriteStatus: string;
      deskStatus: string;
      activeKeepalives: number;
      keepaliveSessions: string[];
    }>(`/desks/${deskId}/sprite-status`),

  // --- Spend ---
  deskSpend: (deskId: string) =>
    apiFetch<SpendResponse>(`/desks/${deskId}/spend`),
  userSpend: () => apiFetch<SpendResponse>("/user/spend"),
};
